import React, { Component } from "react";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { IGroup } from "@formant/core/model/IGroup";

import { groupStore, routerStore } from "../../../stores";
import { localSettingObservable } from "../../../utils/localSettingObservable";
import {
    DataTable,
    defaultSimpleSortState,
    ISimpleSortState,
} from "../../common/DataTable";
import { IDataTableColumn } from "../../common/DataTable/IDataTableColumn";
import { Help } from "../../common/Help";
import { Scrollable } from "../../common/Scrollable";
import {
    applySimpleFilter,
    emptySimpleFilterState,
    ISimpleFilterState,
    SimpleFilter,
} from "../../common/SimpleFilter";
import { StandardButton } from "../../common/StandardButton";
import { StatsHeader } from "../../common/StatsHeader";
import { RowDate } from "../RowDate";
import { RowTitle } from "../RowTitle";
import { SettingsPage } from "../SettingsPage";
import { Actions } from "./Actions";

@observer
export class Groups extends Component {
    @localSettingObservable("settings-groups-filter")
    private filter: ISimpleFilterState = emptySimpleFilterState;

    @localSettingObservable("settings-groups-sort")
    private sort: ISimpleSortState = defaultSimpleSortState;

    public render() {
        const { routeTo } = routerStore;
        const columns: IDataTableColumn<IGroup>[] = [
            {
                header: "Name",
                row: _ => <RowTitle title={_.name} icon="group" />,
                sortBy: _ => _.name,
            },
            {
                header: "Active",
                row: _ => (_.active ? "active" : "inactive"),
                sortBy: _ => Boolean(_.active).toString(),
            },
            {
                header: "Tag key",
                row: _ => _.tagKey,
                sortBy: _ => _.tagKey,
            },
            {
                header: "Tag value",
                row: _ => _.tagValue,
                sortBy: _ => _.tagValue,
            },
            {
                header: "Created",
                row: _ => <RowDate date={_.createdAt} />,
                sortBy: _ => _.createdAt,
                hideBelow: "md",
            },
            {
                header: "Updated",
                row: _ => <RowDate date={_.updatedAt} />,
                sortBy: _ => _.updatedAt,
                hideBelow: "md",
            },
            {
                header: "",
                row: _ => <Actions group={_} />,
            },
        ];

        const groups = groupStore.list();
        const { filter, onChangeFilter, sort, onChangeSort } = this;

        const dimensions = [
            {
                dimensionKey: "tagKey",
                dimensionName: "Tag key",
                getValue: (_: IGroup) => _.tagKey,
            },
            {
                dimensionKey: "tagValue",
                dimensionName: "Tag value",
                getValue: (_: IGroup) => _.tagValue,
            },
            {
                dimensionKey: "active",
                dimensionName: "Active",
                getValue: (_: IGroup) => (_.active ? "active" : "inactive"),
            },
        ];

        const filteredGroups = applySimpleFilter(
            groups || [],
            dimensions,
            _ => [_.name, _.tagKey, _.tagValue].join(" "),
            filter,
        );

        return (
            <SettingsPage
                title="Groups"
                extra={
                    <Help
                        text="A group is a set of related devices. Click for docs."
                        to="https://docs.formant.io/docs/getting-started-create-a-device-group"
                    />
                }
                actions={
                    <StandardButton
                        to={routeTo.createGroup()}
                        data-testid="add-group-button"
                    >
                        Add group
                    </StandardButton>
                }
            >
                <StatsHeader
                    icon="group"
                    loading={!groups}
                    totalQuantity={groups?.length || 0}
                    totalLabel="Groups"
                />
                <SimpleFilter
                    dimensions={dimensions}
                    rows={groups || []}
                    filter={filter}
                    onChangeFilter={onChangeFilter}
                />
                <Scrollable>
                    <DataTable
                        rows={filteredGroups || []}
                        columns={columns}
                        loading={groups === undefined}
                        noResultsText="No groups found"
                        onRowClicked={this.onEdit}
                        sort={sort}
                        onChangeSort={onChangeSort}
                    />
                </Scrollable>
            </SettingsPage>
        );
    }

    private onChangeFilter = (filter: ISimpleFilterState) => {
        this.filter = filter;
    };

    private onChangeSort = (sort: ISimpleSortState) => {
        this.sort = sort;
    };

    private onEdit = async (group: IGroup) => {
        const { navigate, routeTo } = routerStore;
        await navigate(
            routeTo.editGroup({ id: defined(group.id) }, { tab: "Settings" }),
        );
    };
}
